import { useCallback, useEffect, useRef, useState } from 'react';
import { recapToMessage, restoreInteractionEntries, submissionEntry } from './agentInteractionState';
import { T } from 'src/shared/helpers';
import {
    AgentApiError,
    createAgentMessageIdempotencyKey,
    createAgentConversation,
    getAgentConversation,
    getAgentDraft,
    postAgentMessage,
    replaceAgentSideImage,
    deleteAgentPrintArea,
    restoreAgentPrintArea,
    setAgentSideScale,
    upsertAgentPrintArea,
    waitForAgentTurn
} from './agentClient';
import {
    AgentChatEntry,
    AgentTurnRequest,
    AgentQuestionnaireSubmission,
    AgentDraft,
    AgentDraftEditResponse,
    AgentGesture,
    LocalAgentAttachment,
    ReplaceAgentSideImageInput,
    UpsertAgentPrintAreaInput,
    DeleteAgentPrintAreaInput,
    SetAgentSideScaleInput,
    PreviewDraftEdit
} from './types';

const SESSION_STORAGE_KEY = 'zakeke.agent.conversationId';

const emptyDraft: AgentDraft = {
    draftId: null,
    revisionNumber: null,
    title: null,
    document: null
};

const entryId = () => {
    if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') return crypto.randomUUID();
    return `${Date.now()}-${Math.random().toString(36).slice(2)}`;
};

interface RetryableAgentMessage {
    answers?: AgentQuestionnaireSubmission;
    text: string;
    localAttachments: LocalAgentAttachment[];
    gesture: AgentGesture | null;
    idempotencyKey: string;
    conversationId: number | null;
    requestId: number | null;
}

const canRetryRequestError = (error: unknown) => {
    if (!(error instanceof AgentApiError)) return true;
    return ![400, 401, 403, 404, 422].includes(error.status);
};

export const messageForOutcome = (code: string) => {
    const messages: Record<string, string> = {
        max_iterations: T._(
            'The assistant paused after completing as many steps as this turn allows. Its progress was saved; send another message to continue.',
            'Admin'
        ),
        iteration_limit: T._(
            'The assistant paused after completing as many steps as this turn allows. Its progress was saved; send another message to continue.',
            'Admin'
        ),
        max_tool_calls: T._(
            'The assistant paused after completing as many operations as this turn allows. Its progress was saved; send another message to continue.',
            'Admin'
        ),
        tool_call_limit: T._(
            'The assistant paused after completing as many operations as this turn allows. Its progress was saved; send another message to continue.',
            'Admin'
        ),
        token_budget_exhausted: T._(
            'The assistant paused because this request was too large for one turn. Its progress was saved; continue with a shorter message.',
            'Admin'
        ),
        max_output_tokens: T._(
            'The assistant paused because its answer was too large for one turn. Its progress was saved; send another message to continue.',
            'Admin'
        ),
        token_limit: T._(
            'The assistant paused because this request was too large for one turn. Its progress was saved; continue with a shorter message.',
            'Admin'
        ),
        content_filtered: T._('The assistant could not complete this turn because the content was blocked. Nothing new was applied.', 'Admin'),
        no_terminal: T._('The assistant could not complete this turn. Nothing was applied.', 'Admin'),
        turn_in_progress: T._('The request is still being processed.', 'Admin'),
        questionnaire_expired: T._('These questions have been superseded. Review the latest message before answering.', 'Admin')
    };

    return messages[code] ?? T._('The assistant could not complete the request.', 'Admin');
};

export const useAgentSession = (onExternalChanges?: () => void) => {
    const [conversationId, setConversationId] = useState<number | null>(null);
    const [entries, setEntries] = useState<AgentChatEntry[]>([]);
    const [draft, setDraft] = useState<AgentDraft>(emptyDraft);
    const [isBusy, setIsBusy] = useState(false);
    const [currentRequest, setCurrentRequest] = useState<AgentTurnRequest | null>(null);
    const [isRefreshingResult, setIsRefreshingResult] = useState(false);
    const requestBusyRef = useRef(false);
    const [isDraftBusy, setIsDraftBusy] = useState(false);
    const [isDraftRefreshRequired, setIsDraftRefreshRequired] = useState(false);
    const [isRestoring, setIsRestoring] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [retryRequest, setRetryRequest] = useState<RetryableAgentMessage | null>(null);
    const [restoreAttempt, setRestoreAttempt] = useState(0);
    const [restoreFailed, setRestoreFailed] = useState(false);
    const hasPendingRequest = restoreFailed || currentRequest?.state === 'queued' || currentRequest?.state === 'running';
    const requestControllerRef = useRef<AbortController | null>(null);
    const draftEditBusyRef = useRef(false);

    const refreshDraft = useCallback(async (id: number, signal?: AbortSignal) => {
        try {
            const currentDraft = await getAgentDraft(id, signal);
            if (signal?.aborted) return false;
            setDraft(currentDraft ?? emptyDraft);
            setIsDraftRefreshRequired(false);
            return true;
        } catch {
            if (signal?.aborted) return false;
            // A preview failure must not turn a successful conversation turn into a failed one.
            setIsDraftRefreshRequired(true);
            return false;
        }
    }, []);

    useEffect(() => {
        const restore = async () => {
            setRestoreFailed(false);
            setIsRestoring(true);
            const storedId = Number(window.sessionStorage.getItem(SESSION_STORAGE_KEY));
            if (!Number.isFinite(storedId) || storedId <= 0) {
                setIsRestoring(false);
                return;
            }

            const controller = new AbortController();
            requestControllerRef.current = controller;

            try {
                const conversation = await getAgentConversation(storedId, controller.signal);
                if (controller.signal.aborted) return;
                if (!conversation) {
                    window.sessionStorage.removeItem(SESSION_STORAGE_KEY);
                    return;
                }

                setConversationId(conversation.conversationId);
                setEntries(restoreInteractionEntries(conversation));
                await refreshDraft(conversation.conversationId, controller.signal);
                let pending = conversation.requests?.find((item) => item.state === 'queued' || item.state === 'running');
                while (pending && !controller.signal.aborted) {
                    requestBusyRef.current = true;
                    setIsBusy(true);
                    setIsRestoring(false);
                    setCurrentRequest(pending);
                    const active = pending;
                    try {
                        const completed = await waitForAgentTurn(storedId, active.requestId, controller.signal, (update) => {
                            if (!controller.signal.aborted) setCurrentRequest(update);
                        });
                        if (controller.signal.aborted) return;
                        if (completed.turn?.hasExternalChanges) onExternalChanges?.();
                        setIsRefreshingResult(true);
                        await refreshDraft(storedId, controller.signal);
                        const refreshed = await getAgentConversation(storedId, controller.signal);
                        if (controller.signal.aborted) return;
                        if (refreshed) setEntries(restoreInteractionEntries(refreshed));
                        pending = refreshed?.requests?.find((item) => item.state === 'queued' || item.state === 'running');
                    } catch (pollError) {
                        if (controller.signal.aborted) return;
                        setError((pollError as Error).message);
                        setRetryRequest({
                            text: active.submission?.text ?? '',
                            localAttachments: [],
                            gesture: null,
                            idempotencyKey: createAgentMessageIdempotencyKey(),
                            conversationId: storedId,
                            requestId: active.requestId
                        });
                        break;
                    } finally {
                        if (!controller.signal.aborted) setIsRefreshingResult(false);
                    }
                }
            } catch (restoreError) {
                if (controller.signal.aborted) return;
                if (restoreError instanceof AgentApiError && [403, 404].includes(restoreError.status)) {
                    window.sessionStorage.removeItem(SESSION_STORAGE_KEY);
                } else {
                    setRestoreFailed(true);
                    setError(T._('The conversation could not be refreshed. Check again to resume it.', 'Admin'));
                }
            } finally {
                if (!controller.signal.aborted) {
                    requestBusyRef.current = false;
                    setIsRestoring(false);
                    setIsBusy(false);
                }
            }
        };

        void restore();
        return () => requestControllerRef.current?.abort();
    }, [refreshDraft, restoreAttempt]);

    const ensureConversation = useCallback(
        async (signal: AbortSignal) => {
            if (conversationId) return conversationId;

            const conversation = await createAgentConversation(`${T._('AI assistant', 'Admin')} - ${new Date().toLocaleString()}`, signal);
            if (signal.aborted) throw new DOMException('Aborted', 'AbortError');
            setConversationId(conversation.conversationId);
            window.sessionStorage.setItem(SESSION_STORAGE_KEY, String(conversation.conversationId));
            return conversation.conversationId;
        },
        [conversationId]
    );

    const submitMessage = useCallback(
        async (request: RetryableAgentMessage, appendMerchantEntry: boolean) => {
            if (requestBusyRef.current || draftEditBusyRef.current || (hasPendingRequest && request.requestId !== currentRequest?.requestId)) return false;
            requestBusyRef.current = true;

            setError(null);
            setRetryRequest(null);
            setIsBusy(true);
            requestControllerRef.current?.abort();
            const controller = new AbortController();
            requestControllerRef.current = controller;
            let retryCandidate = request;
            let accepted = Boolean(request.requestId);
            const optimisticId = request.idempotencyKey;
            if (!request.requestId) setCurrentRequest(null);

            if (appendMerchantEntry && !request.answers) {
                setEntries((current) => [
                    ...current,
                    {
                        id: optimisticId,
                        role: 'merchant',
                        text: request.text,
                        attachments: request.localAttachments
                    }
                ]);
            }

            try {
                const id = request.conversationId ?? (await ensureConversation(controller.signal));
                retryCandidate = { ...retryCandidate, conversationId: id };

                let requestId = request.requestId;
                if (!requestId) {
                    const queued = await postAgentMessage(
                        id,
                        request.text,
                        request.localAttachments.map(({ url, name, side, variant }) => ({ url, name, side, variant })),
                        request.gesture,
                        request.idempotencyKey,
                        controller.signal,
                        request.answers
                    );
                    if (controller.signal.aborted) return false;
                    requestId = queued.requestId;
                    accepted = true;
                    setCurrentRequest(queued);
                    const confirmedEntry = submissionEntry(queued);
                    if (confirmedEntry)
                        setEntries((current) => [...current.filter((entry) => entry.id !== optimisticId && entry.id !== confirmedEntry.id), confirmedEntry]);
                    retryCandidate = { ...retryCandidate, requestId };
                }

                const completed = await waitForAgentTurn(id, requestId, controller.signal, (update) => {
                    if (!controller.signal.aborted) setCurrentRequest(update);
                });
                if (controller.signal.aborted) return accepted;
                setIsRefreshingResult(true);
                await refreshDraft(id, controller.signal);
                if (controller.signal.aborted) return accepted;

                if (completed.state === 'completed' && completed.turn) {
                    if (completed.turn.hasExternalChanges) onExternalChanges?.();
                    setEntries((current) => [
                        ...current.filter((entry) => entry.role !== 'agent' || entry.turn.turnId !== completed.turn!.turnId),
                        { id: entryId(), role: 'agent', turn: completed.turn! }
                    ]);
                } else {
                    const code = completed.failureCode ?? 'no_terminal';
                    setEntries((current) => [...current, { id: entryId(), role: 'notice', code, message: messageForOutcome(code) }]);
                    setRetryRequest({
                        ...retryCandidate,
                        text: completed.submission?.answers ? recapToMessage(completed.submission.answers) : retryCandidate.text,
                        answers: undefined,
                        idempotencyKey: createAgentMessageIdempotencyKey(),
                        requestId: null
                    });
                }
            } catch (sendError) {
                if ((sendError as Error).name !== 'AbortError') {
                    const message = (sendError as Error).message || T._('The message could not be delivered.', 'Admin');
                    setError(message);
                    if (canRetryRequestError(sendError)) setRetryRequest(retryCandidate);
                    if (sendError instanceof AgentApiError && sendError.status === 409 && request.answers) {
                        setRetryRequest(null);
                        const latest = await getAgentConversation(request.conversationId!, controller.signal).catch(() => null);
                        if (latest && !controller.signal.aborted) setEntries(restoreInteractionEntries(latest));
                    }
                }
            } finally {
                if (!controller.signal.aborted) {
                    requestBusyRef.current = false;
                    setIsBusy(false);
                    setIsRefreshingResult(false);
                }
            }
            return accepted;
        },
        [ensureConversation, hasPendingRequest, currentRequest?.requestId, onExternalChanges, refreshDraft]
    );

    const send = useCallback(
        async (text: string, localAttachments: LocalAgentAttachment[] = [], gesture: AgentGesture | null = null) => {
            const trimmedText = text.trim();
            if (isBusy || hasPendingRequest || draftEditBusyRef.current || (!trimmedText && localAttachments.length === 0 && !gesture)) return;

            await submitMessage(
                {
                    text: trimmedText,
                    localAttachments,
                    gesture,
                    idempotencyKey: createAgentMessageIdempotencyKey(),
                    conversationId,
                    requestId: null
                },
                true
            );
        },
        [conversationId, isBusy, hasPendingRequest, submitMessage]
    );

    const retry = useCallback(async () => {
        if (restoreFailed) {
            setError(null);
            setRestoreAttempt((attempt) => attempt + 1);
            return;
        }
        if (!retryRequest) return;
        await submitMessage(retryRequest, false);
    }, [retryRequest, restoreFailed, submitMessage]);

    const sendAnswers = useCallback(
        async (answers: AgentQuestionnaireSubmission) => {
            if (!conversationId) return false;
            return await submitMessage(
                { text: '', answers, localAttachments: [], gesture: null, idempotencyKey: createAgentMessageIdempotencyKey(), conversationId, requestId: null },
                true
            );
        },
        [conversationId, submitMessage]
    );

    const editDraft = useCallback(
        async (edit: (id: number, revisionNumber: number) => Promise<AgentDraftEditResponse>, expectedRevisionNumber?: number) => {
            if (!conversationId || !draft.revisionNumber || isBusy || hasPendingRequest || draftEditBusyRef.current) return null;

            setError(null);
            draftEditBusyRef.current = true;
            setIsDraftBusy(true);
            try {
                const result = await edit(conversationId, expectedRevisionNumber ?? draft.revisionNumber);
                if (!(await refreshDraft(conversationId))) {
                    setError(T._('Your change was saved, but the preview could not be refreshed. Refresh the preview before editing again.', 'Admin'));
                }
                return result;
            } catch (draftError) {
                if (draftError instanceof AgentApiError && draftError.status === 409) {
                    await refreshDraft(conversationId);
                }
                setError((draftError as Error).message || T._('The draft could not be updated.', 'Admin'));
                return null;
            } finally {
                draftEditBusyRef.current = false;
                setIsDraftBusy(false);
            }
        },
        [conversationId, draft.revisionNumber, isBusy, hasPendingRequest, refreshDraft]
    );

    const upsertPrintArea = useCallback(
        (input: PreviewDraftEdit<UpsertAgentPrintAreaInput>) =>
            editDraft((id, expectedRevisionNumber) => upsertAgentPrintArea(id, { ...input, expectedRevisionNumber }), input.expectedRevisionNumber),
        [editDraft]
    );

    const replaceSideImage = useCallback(
        (input: PreviewDraftEdit<ReplaceAgentSideImageInput>) =>
            editDraft((id, expectedRevisionNumber) => replaceAgentSideImage(id, { ...input, expectedRevisionNumber }), input.expectedRevisionNumber),
        [editDraft]
    );

    const deletePrintArea = useCallback(
        (input: PreviewDraftEdit<DeleteAgentPrintAreaInput>) =>
            editDraft((id, expectedRevisionNumber) => deleteAgentPrintArea(id, { ...input, expectedRevisionNumber }), input.expectedRevisionNumber),
        [editDraft]
    );
    const restorePrintArea = useCallback(
        (input: PreviewDraftEdit<DeleteAgentPrintAreaInput>) =>
            editDraft((id, expectedRevisionNumber) => restoreAgentPrintArea(id, { ...input, expectedRevisionNumber }), input.expectedRevisionNumber),
        [editDraft]
    );
    const setSideScale = useCallback(
        (input: PreviewDraftEdit<SetAgentSideScaleInput>) =>
            editDraft((id, expectedRevisionNumber) => setAgentSideScale(id, { ...input, expectedRevisionNumber }), input.expectedRevisionNumber),
        [editDraft]
    );

    const reset = useCallback(() => {
        requestControllerRef.current?.abort();
        window.sessionStorage.removeItem(SESSION_STORAGE_KEY);
        setConversationId(null);
        setEntries([]);
        setDraft(emptyDraft);
        setError(null);
        setRetryRequest(null);
        setRestoreFailed(false);
        setIsRestoring(false);
        setIsBusy(false);
        requestBusyRef.current = false;
        setCurrentRequest(null);
        setIsRefreshingResult(false);
        draftEditBusyRef.current = false;
        setIsDraftBusy(false);
        setIsDraftRefreshRequired(false);
    }, []);

    const refreshPreview = useCallback(async () => {
        if (!conversationId || isBusy || draftEditBusyRef.current) return;
        setIsDraftBusy(true);
        try {
            if (await refreshDraft(conversationId)) setError(null);
            else setError(T._('The preview could not be refreshed. Please try again.', 'Admin'));
        } finally {
            setIsDraftBusy(false);
        }
    }, [conversationId, isBusy, refreshDraft]);

    return {
        conversationId,
        currentRequest,
        isRefreshingResult,
        sendAnswers,
        entries,
        draft,
        isBusy,
        hasPendingRequest,
        isDraftBusy,
        isDraftRefreshRequired,
        refreshPreview,
        isRestoring,
        error,
        canRetry: restoreFailed || retryRequest !== null,
        send,
        retry,
        upsertPrintArea,
        replaceSideImage,
        deletePrintArea,
        restorePrintArea,
        setSideScale,
        reset
    };
};
